import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiHeart, FiMinus, FiPlus, FiShield, FiShoppingBag, FiTruck } from "react-icons/fi";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, wishlist, openCart } = useContext(CartContext);
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <span>404</span>
        <h1>Product not found</h1>
        <p>The tool you’re looking for may have moved or is no longer in the collection.</p>
        <Link to="/shop">Back to shop</Link>
      </main>
    );
  }

  const wished = wishlist.includes(product.id);
  const galleryImages = product.images.length > 1 ? product.images : [product.image, product.image];
  const related = productData.filter((item) => item.category === product.category && item.id !== product.id).slice(0, 5);

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedVariant, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
    openCart();
  };

  return (
    <main className="product-detail">
      <nav className="product-detail__breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link><span>→</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link><span>→</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="product-detail__layout">
        <section className="product-detail__gallery" aria-label={`${product.name} image gallery`}>
          <div className="product-detail__main-image">
            <img src={galleryImages[selectedImage]} alt={product.name} width="1254" height="1254" />
            <button type="button" aria-label="Previous image" onClick={() => setSelectedImage((current) => (current - 1 + galleryImages.length) % galleryImages.length)}><FiChevronLeft /></button>
            <button type="button" aria-label="Next image" onClick={() => setSelectedImage((current) => (current + 1) % galleryImages.length)}><FiChevronRight /></button>
          </div>
          <div className="product-detail__thumbs">
            {galleryImages.map((image, index) => (
              <button
                type="button"
                key={`${image}-${index}`}
                className={index === selectedImage ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show product image ${index + 1}`}
                aria-pressed={index === selectedImage}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>
        </section>

        <aside className="product-detail__info">
          <p className="product-detail__stock"><i /> In stock — ready to dispatch</p>
          <p className="product-detail__brand">{product.brand}</p>
          <h1>{product.name}</h1>
          <div className="product-detail__rating" aria-label={`Rated ${product.rating} out of 5 from ${product.reviews} reviews`}>
            <span>★★★★★</span> {product.rating} · {product.reviews} reviews
          </div>
          <div className="product-detail__price">
            <strong>{formatPrice(product.price)}</strong><span>USD</span>
          </div>

          <p className="product-detail__intro">{product.description}</p>

          {product.colors.length > 0 && (
            <fieldset className="product-detail__choices">
              <legend>Colour: <strong>{selectedColor}</strong></legend>
              <div>
                {product.colors.map((color) => (
                  <button type="button" key={color} className={selectedColor === color ? "is-selected" : ""} onClick={() => setSelectedColor(color)} aria-pressed={selectedColor === color}>{color}</button>
                ))}
              </div>
            </fieldset>
          )}

          {product.variants.length > 0 && (
            <fieldset className="product-detail__choices">
              <legend>Format: <strong>{selectedVariant}</strong></legend>
              <div>
                {product.variants.map((variant) => (
                  <button type="button" key={variant} className={selectedVariant === variant ? "is-selected" : ""} onClick={() => setSelectedVariant(variant)} aria-pressed={selectedVariant === variant}>{variant}</button>
                ))}
              </div>
            </fieldset>
          )}

          <div className="product-detail__buy-row">
            <div className="product-detail__quantity" aria-label="Quantity selector">
              <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity((current) => Math.max(1, current - 1))}><FiMinus /></button>
              <span aria-live="polite">{quantity}</span>
              <button type="button" aria-label="Increase quantity" onClick={() => setQuantity((current) => current + 1)}><FiPlus /></button>
            </div>
            <button type="button" className={`product-detail__add ${added ? "is-added" : ""}`} onClick={handleAddToCart}>
              <FiShoppingBag /> {added ? "Added to basket" : "Add to basket"}
            </button>
            <button type="button" className={`product-detail__wishlist ${wished ? "is-active" : ""}`} onClick={() => toggleWishlist(product.id)} aria-label={`${wished ? "Remove from" : "Add to"} wishlist`} aria-pressed={wished}><FiHeart /></button>
          </div>

          <div className="product-detail__trust">
            <div><FiTruck /><span><strong>Tracked delivery</strong>{siteConfig.shipping.message}</span></div>
            <div><FiShield /><span><strong>Easy returns</strong>14 days to change your mind</span></div>
          </div>

          <details className="product-detail__details" open>
            <summary>Product details</summary>
            <dl>
              {Object.entries(product.attributes).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}
              <div><dt>Product code</dt><dd>PS-{String(product.id).padStart(4, "0")}</dd></div>
            </dl>
          </details>
          <details className="product-detail__details">
            <summary>Delivery & returns</summary>
            <p>Tracked delivery is a fixed {formatPrice(siteConfig.shipping.standard)} per order. Eligible unused items can be returned within 14 days.</p>
          </details>
        </aside>
      </div>

      <section className="product-detail__related" aria-labelledby="related-title">
        <div><h2 id="related-title">More from {product.category}</h2><Link to={`/shop?category=${encodeURIComponent(product.category)}`}>View all →</Link></div>
        <div>
          {related.map((item) => (
            <article key={item.id}>
              <Link to={`/shopDetails/${item.id}`}><img src={item.image} alt={item.name} loading="lazy" /><span>{item.brand}</span><h3>{item.name}</h3><strong>{formatPrice(item.price)}</strong></Link>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
